import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Paper, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { AestheticsParams } from '../../../services/aesthetics';
import { saveAestheticsAction } from '../../../redux/actions/investigationsActions';

const defaultAesthetics = (): AestheticsParams => ({
    primary: { color: '#49CEBF', background: '#E0F7F4' },
    secondary: { color: '#1976D2', background: '#E3F2FD' },
    terciary: { color: '#F57C00', background: '#FFF3E0' },
    sidebar: { color: '#FFFFFF', background: '#233044' },
    nextButton: { color: '#FFFFFF', background: '#1976D2' },
    header: { background: '#FFFFFF' },
});

// Colour+background levels rendered as a pair of pickers each.
const ELEMENT_LEVELS = ['primary', 'secondary', 'terciary', 'sidebar', 'nextButton'] as const;
type ElementLevel = typeof ELEMENT_LEVELS[number];

const Aesthetics: React.FC = () => {
    const dispatch = useDispatch();
    const investigation = useSelector((state: any) => state.investigations.currentInvestigation);
    const [params, setParams] = useState<AestheticsParams>(defaultAesthetics());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (investigation?.aesthetics?.params) {
            // Start from the hardcoded defaults, then overlay whatever is saved — deep-merged
            // per level so a partially-saved level (e.g. only `color`) still falls back to the
            // default for its missing fields, on every level (not just primary).
            const def = defaultAesthetics();
            const saved = investigation.aesthetics.params;
            setParams({
                ...def,
                ...saved,
                primary: { ...def.primary, ...(saved.primary ?? {}) },
                secondary: { ...def.secondary, ...(saved.secondary ?? {}) },
                terciary: { ...def.terciary, ...(saved.terciary ?? {}) },
                sidebar: { ...def.sidebar, ...(saved.sidebar ?? {}) },
                nextButton: { ...def.nextButton, ...(saved.nextButton ?? {}) },
                header: { ...def.header, ...(saved.header ?? {}) },
            });
        }
    }, [investigation?.aesthetics]);

    const updateElement = (
        key: ElementLevel,
        field: 'color' | 'background',
        value: string
    ) => {
        setParams({
            ...params,
            [key]: {
                ...(params[key] ?? defaultAesthetics()[key]),
                [field]: value,
            },
        });
    };

    const handleSave = async () => {
        if (!investigation?.uuid) return;
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            await dispatch(saveAestheticsAction(investigation.uuid, params) as any);
            setSuccess(true);
        } catch {
            setError('save_failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Translate id="hospital.demo_configuration.aesthetics.save_failed" />
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Translate id="hospital.demo_configuration.save_success" />
                </Alert>
            )}
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    {ELEMENT_LEVELS.map((level) => (
                        <React.Fragment key={level}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={`${level} color`}
                                    type="color"
                                    size="small"
                                    fullWidth
                                    value={params[level]?.color ?? '#000000'}
                                    onChange={(e) => updateElement(level, 'color', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={`${level} background`}
                                    type="color"
                                    size="small"
                                    fullWidth
                                    value={params[level]?.background ?? '#FFFFFF'}
                                    onChange={(e) => updateElement(level, 'background', e.target.value)}
                                />
                            </Grid>
                        </React.Fragment>
                    ))}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="header background"
                            type="color"
                            size="small"
                            fullWidth
                            value={params.header?.background ?? '#FFFFFF'}
                            onChange={(e) => setParams({
                                ...params,
                                header: { ...(params.header ?? { background: '#FFFFFF' }), background: e.target.value },
                            })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : <Translate id="general.save" />}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default Aesthetics;
